import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "../App";
import { useTranslation } from "react-i18next";
import {
  Activity, Shield, Bell, ClipboardCheck, Users,
  Siren, PenTool, FileText, BarChart3, FolderOpen,
  Upload, MessageSquare, CheckCircle2, X, Compass,
  ArrowRight, Sparkles, ChevronRight, RotateCcw
} from "lucide-react";

const GUIDE_CONFIG = {
  admin: {
    titleKey: "guide:quickStart.admin.title",
    subtitleKey: "guide:quickStart.admin.subtitle",
    cards: [
      { id: "systemHealth", icon: Activity, titleKey: "guide:cards.systemHealth.title", descKey: "guide:cards.systemHealth.description", path: "/dashboard", color: "#4ADE80" },
      { id: "governance", icon: Shield, titleKey: "guide:cards.governance.title", descKey: "guide:cards.governance.description", path: "/governance", color: "#36A9E1" },
      { id: "qaSupervisor", icon: ClipboardCheck, titleKey: "guide:cards.qaSupervisor.title", descKey: "guide:cards.qaSupervisor.description", path: "/qa-supervisor", color: "#E8A838" },
      { id: "notifications", icon: Bell, titleKey: "guide:cards.notifications.title", descKey: "guide:cards.notifications.description", path: "/notification-log", color: "#A78BFA" },
      { id: "onboardingReview", icon: Users, titleKey: "guide:cards.onboardingReview.title", descKey: "guide:cards.onboardingReview.description", path: "/client-portal", color: "#F472B6" },
    ],
  },
  auditor: {
    titleKey: "guide:quickStart.auditor.title",
    subtitleKey: "guide:quickStart.auditor.subtitle",
    cards: [
      { id: "auditConductor", icon: Siren, titleKey: "guide:cards.auditConductor.title", descKey: "guide:cards.auditConductor.description", path: "/audit-conductor", color: "#F87171" },
      { id: "auditChecklist", icon: ClipboardCheck, titleKey: "guide:cards.auditChecklist.title", descKey: "guide:cards.auditChecklist.description", path: "/audits", color: "#36A9E1" },
      { id: "scriber", icon: PenTool, titleKey: "guide:cards.scriber.title", descKey: "guide:cards.scriber.description", path: "/scriber", color: "#4ADE80" },
      { id: "findingsCapa", icon: FileText, titleKey: "guide:cards.findingsCapa.title", descKey: "guide:cards.findingsCapa.description", path: "/capa", color: "#E8A838" },
      { id: "reportsExport", icon: BarChart3, titleKey: "guide:cards.reportsExport.title", descKey: "guide:cards.reportsExport.description", path: "/reports", color: "#A78BFA" },
    ],
  },
  client: {
    titleKey: "guide:quickStart.client.title",
    subtitleKey: "guide:quickStart.client.subtitle",
    cards: [
      { id: "myOnboarding", icon: ClipboardCheck, titleKey: "guide:cards.myOnboarding.title", descKey: "guide:cards.myOnboarding.description", path: "/client-onboarding", color: "#36A9E1" },
      { id: "uploadDocuments", icon: Upload, titleKey: "guide:cards.uploadDocuments.title", descKey: "guide:cards.uploadDocuments.description", path: "/documents", color: "#4ADE80" },
      { id: "reviewRequests", icon: MessageSquare, titleKey: "guide:cards.reviewRequests.title", descKey: "guide:cards.reviewRequests.description", path: "/messages", color: "#E8A838" },
      { id: "readinessStatus", icon: CheckCircle2, titleKey: "guide:cards.readinessStatus.title", descKey: "guide:cards.readinessStatus.description", path: "/client-portal", color: "#A78BFA" },
      { id: "helpCenter", icon: FolderOpen, titleKey: "guide:cards.helpCenter.title", descKey: "guide:cards.helpCenter.description", path: "/knowledge", color: "#F472B6" },
    ],
  },
};

const QuickStartGuide = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(["guide", "common"]);
  const [guideState, setGuideState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const role = user?.role || "client";
  const config = GUIDE_CONFIG[role] || GUIDE_CONFIG.client;

  const fetchGuideState = useCallback(async () => {
    try {
      const res = await fetch(`${API}/user-guide/state`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setGuideState(data);
        if (data.status === "dismissed" || data.status === "completed") {
          setDismissed(true);
        }
      }
    } catch (e) {
      console.error("Guide state fetch failed:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchGuideState(); }, [fetchGuideState]);

  const updateState = async (updates) => {
    const newState = { ...guideState, ...updates };
    setGuideState(newState);
    try {
      await fetch(`${API}/user-guide/state`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newState),
      });
    } catch (e) {
      console.error("Guide state update failed:", e);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    updateState({ status: "dismissed" });
  };

  const handleCardClick = (card) => {
    const completed = guideState?.completed_cards || [];
    if (!completed.includes(card.id)) {
      updateState({ completed_cards: [...completed, card.id] });
    }
    navigate(card.path);
  };

  if (loading) return null;
  if (dismissed) return null;

  const completedCards = guideState?.completed_cards || [];
  const progress = Math.round((completedCards.length / config.cards.length) * 100);
  const allComplete = completedCards.length === config.cards.length;

  return (
    <div className="mb-6" data-testid="quick-start-guide">
      <div className="relative overflow-hidden rounded-lg border border-[rgba(255,255,255,0.08)] bg-gradient-to-br from-[#1A1D27] to-[#15171E]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#36A9E1]/20 to-[#A78BFA]/20 flex items-center justify-center">
              <Compass size={18} className="text-[#36A9E1]" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-base font-['Syne']" data-testid="guide-title">
                {t(config.titleKey)}
              </h3>
              <p className="text-[#8B95A5] text-xs mt-0.5">{t(config.subtitleKey)}</p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1.5 rounded-md text-[#8B95A5] hover:text-white hover:bg-[rgba(255,255,255,0.06)] transition-colors"
            data-testid="guide-dismiss-btn"
            title={t("guide:quickStart.dismiss")}
          >
            <X size={16} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-5 pb-3">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="flex-1 h-1 bg-[rgba(255,255,255,0.06)] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${progress}%`,
                  background: allComplete
                    ? "linear-gradient(90deg, #4ADE80, #36A9E1)"
                    : "linear-gradient(90deg, #36A9E1, #A78BFA)",
                }}
                data-testid="guide-progress-bar"
              />
            </div>
            <span className="text-[#8B95A5] text-xs font-mono" data-testid="guide-progress-text">
              {t("guide:quickStart.progressOf", { done: completedCards.length, total: config.cards.length })}
            </span>
          </div>
        </div>

        {/* Cards grid */}
        <div className="px-5 pb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {config.cards.map((card) => {
            const Icon = card.icon;
            const isCompleted = completedCards.includes(card.id);
            return (
              <button
                key={card.id}
                onClick={() => handleCardClick(card)}
                className={`group relative text-left p-3.5 rounded-lg border transition-all duration-200 ${
                  isCompleted
                    ? "border-[rgba(74,222,128,0.2)] bg-[rgba(74,222,128,0.04)]"
                    : "border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)] hover:border-[rgba(255,255,255,0.15)] hover:bg-[rgba(255,255,255,0.04)]"
                }`}
                data-testid={`guide-card-${card.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div
                    className="w-8 h-8 rounded-md flex items-center justify-center"
                    style={{ backgroundColor: `${card.color}15` }}
                  >
                    <Icon size={16} style={{ color: card.color }} />
                  </div>
                  {isCompleted ? (
                    <CheckCircle2 size={14} className="text-[#4ADE80]" />
                  ) : (
                    <ArrowRight size={14} className="text-[#8B95A5] opacity-0 group-hover:opacity-100 transition-opacity" />
                  )}
                </div>
                <h4 className="text-white text-sm font-medium mb-1">{t(card.titleKey)}</h4>
                <p className="text-[#8B95A5] text-xs leading-relaxed line-clamp-2">{t(card.descKey)}</p>
              </button>
            );
          })}
        </div>

        {/* All complete message */}
        {allComplete && (
          <div className="px-5 pb-4" data-testid="guide-complete-message">
            <div className="flex items-center gap-2 p-3 rounded-lg bg-[rgba(74,222,128,0.06)] border border-[rgba(74,222,128,0.15)]">
              <Sparkles size={14} className="text-[#4ADE80]" />
              <span className="text-[#4ADE80] text-sm">
                {t("guide:quickStart.allComplete")}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Minimal sidebar/help entry for reopening guide
export const GuideReopenButton = ({ onClick }) => {
  const { t } = useTranslation(["guide"]);
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-2 text-[#8B95A5] hover:text-white hover:bg-[rgba(255,255,255,0.04)] rounded-[3px] transition-colors text-sm"
      data-testid="guide-reopen-btn"
    >
      <Compass size={14} />
      <span>{t("guide:sidebar.gettingStarted")}</span>
      <ChevronRight size={12} className="ml-auto" />
    </button>
  );
};

// Reset button for sidebar
export const GuideResetButton = ({ onReset }) => {
  const { t } = useTranslation(["guide"]);
  return (
    <button
      onClick={onReset}
      className="w-full flex items-center gap-2 px-3 py-2 text-[#8B95A5] hover:text-white hover:bg-[rgba(255,255,255,0.04)] rounded-[3px] transition-colors text-sm"
      data-testid="guide-reset-btn"
    >
      <RotateCcw size={14} />
      <span>{t("guide:sidebar.resetGuide")}</span>
    </button>
  );
};

export default QuickStartGuide;
